import { useState, useCallback, useMemo, useRef, memo } from 'react';
import { useSelector } from '@xstate/store/react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Plus } from 'lucide-react';
import { useCanvasStores, useCanvasLayers } from '../../stores/CanvasContext';
import { globalTasksStore } from '../../../stores/globalTasksStore';
import { useCanvasContextMenus } from '../../hooks/useCanvasContextMenus';
import { useAddSubmenuItems } from '../../hooks/useAddSubmenuItems';
import { generateLayerId } from '../../types';
import type { GroupData } from '../../entityTypes';

import type { LayerTreeItem } from './types';
import { parseDragId } from './types';
import { buildParentMaps, buildLayerTree, materializeForLayerPanel } from './buildLayerTree';
import { handleLayerPanelDragEnd } from './LayerPanelDragHandler';
import { LayerHeaderRow } from './LayerHeaderRow';
import { DraggableItemRow } from './DraggableTreeItem';

// Stable sensor config (module level prevents re-creation on every render)
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };

// ============================================
// Droppable Layer Zone (inline — only used here)
// ============================================

const DroppableLayerZone = memo(function DroppableLayerZone({ layerId, children }: { layerId: string; children: React.ReactNode }) {
  const dropId = `layer:${layerId}`;
  const { setNodeRef, isOver } = useDroppable({ id: dropId });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[8px] ${isOver ? 'bg-blue-50' : ''}`}
    >
      {children}
    </div>
  );
});

// ============================================
// Main Layer Panel Orchestrator
// ============================================

export function CanvasLayerPanelContent() {
  const { t } = useTranslation();
  const { store, uiStore } = useCanvasStores();
  const layers = useCanvasLayers();
  const contextMenus = useCanvasContextMenus();
  const { buildAddSubmenuItems } = useAddSubmenuItems();

  // Structural key: stable string during drag
  const structuralKey = useSelector(store, s => {
    const nodes = Object.values(s.context.nodes);
    const groups = Object.values(s.context.groups);
    let k = '';
    for (const e of nodes) {
      k += `${e.id}:${e.kind}:${e.layerId}|`;
    }
    for (const e of groups) {
      k += `${e.id}:${e.kind}:${e.layerId}|`;
      const gd = e.data as unknown as GroupData;
      k += `g:${gd.label}:${gd.memberEntityIds.join(',')}:${gd.collapsed}:${gd.displayMode ?? 'invisible'}|`;
    }
    return k;
  });

  const globalTasksById = useSelector(globalTasksStore, s => s.context.tasksById);

  // Compute entities/groups/topLevelEntityOrder — only when structure changes
  const { entities, groups, topLevelEntityOrder } = useMemo(() => {
    const ctx = store.getSnapshot().context;
    const rawEntities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.groups),
    ];
    return materializeForLayerPanel(rawEntities, globalTasksById);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structuralKey, globalTasksById]);

  // Stable callback wrapper for context menu
  const contextMenusRef = useRef(contextMenus);
  contextMenusRef.current = contextMenus;
  const stableHandleEntityContextMenu = useCallback((entityId: string, e: React.MouseEvent) => {
    contextMenusRef.current.handleEntityContextMenu(entityId, e);
  }, []);

  const collapsedLayers = useSelector(uiStore, s => s.context.layerPanelCollapsedLayers);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sortedLayers = useMemo(() => [...layers].reverse(), [layers]);
  const parentMaps = useMemo(() => buildParentMaps(groups), [groups]);

  const layerTrees = useMemo(() => {
    const map = new Map<string, LayerTreeItem[]>();
    for (const layer of sortedLayers) {
      map.set(layer.id, buildLayerTree(layer.id, entities, globalTasksById, groups, parentMaps, topLevelEntityOrder));
    }
    return map;
  }, [entities, groups, globalTasksById, parentMaps, topLevelEntityOrder, sortedLayers]);

  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS));

  const handleAdd = useCallback(() => {
    const newLayer = {
      id: generateLayerId(),
      name: `Layer ${layers.length + 1}`,
      visible: true,
      locked: false,
    };
    store.send({ type: 'layer.add', layer: newLayer });
    store.send({ type: 'layer.setActive', layerId: newLayer.id });
  }, [store, layers]);

  // --- DnD handlers ---

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback((event: import('@dnd-kit/core').DragEndEvent) => {
    setActiveDragId(null);
    handleLayerPanelDragEnd(event, store);
  }, [store]);

  // Flat label map from layer trees (reuses already-built tree data)
  const labelMap = useMemo(() => {
    const map = new Map<string, string>();
    function collect(items: LayerTreeItem[]) {
      for (const item of items) {
        map.set(item.id, item.label);
        if (item.children) collect(item.children);
      }
    }
    for (const items of layerTrees.values()) collect(items);
    return map;
  }, [layerTrees]);

  const activeDragLabel = useMemo(() => {
    if (!activeDragId) return null;
    const { id } = parseDragId(activeDragId);
    return labelMap.get(id) ?? id;
  }, [activeDragId, labelMap]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 shrink-0">
        <span className="text-xs font-medium text-gray-700">{t('canvasLayer.layers')}</span>
        <button
          onClick={handleAdd}
          className="p-0.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-50"
          title={t('canvasLayer.addLayer')}
        >
          <Plus size={12} />
        </button>
      </div>

      {/* Layer list with tree */}
      <div className="overflow-y-auto flex-1">
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {sortedLayers.map(layer => {
            const isCollapsed = collapsedLayers.has(layer.id);
            const treeItems = layerTrees.get(layer.id) ?? [];

            return (
              <div key={layer.id} className="border-b border-gray-100">
                <LayerHeaderRow layerId={layer.id} />

                {!isCollapsed && (
                  <DroppableLayerZone layerId={layer.id}>
                    {treeItems.map(item => (
                      <DraggableItemRow
                        key={`${item.kind}-${item.id}`}
                        item={item}
                        depth={0}
                        onItemContextMenu={stableHandleEntityContextMenu}
                      />
                    ))}
                    {treeItems.length === 0 && (
                      <div className="text-[10px] text-gray-300 italic py-1 px-3">
                        {t('canvasLayer.empty')}
                      </div>
                    )}
                    {/* Add entity button */}
                    <button
                      className="flex items-center gap-1 w-full px-3 py-0.5 text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        uiStore.send({ type: 'contextMenu.set', menu: { x: rect.right, y: rect.top, items: buildAddSubmenuItems(layer.id) } });
                      }}
                    >
                      <Plus size={9} />
                      <span>{t('canvasLayer.add')}</span>
                    </button>
                  </DroppableLayerZone>
                )}
              </div>
            );
          })}

          <DragOverlay>
            {activeDragId && (
              <div className="bg-white border border-blue-300 rounded px-2 py-1 text-xs text-gray-700 shadow-md whitespace-nowrap">
                {activeDragLabel}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
